import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const DANGER_RED = '#B91C1C';
const DANGER_RED_HOVER = '#991B1B';

const PaymentProviderDisconnectModal = ({
  open,
  onClose,
  onSuccess,
  providerLabel,
  onDisconnect,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    setError('');
    setSubmitting(true);
    try {
      await onDisconnect();
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err?.message || `Failed to disconnect ${providerLabel}.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setError('');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Disconnect {providerLabel}?
          </Typography>
          <IconButton onClick={handleClose} size="small" disabled={submitting} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="body1" color="text.primary" sx={{ mb: 2 }}>
          Are you sure you want to disconnect{' '}
          <Box component="span" sx={{ fontWeight: 700 }}>
            {providerLabel}
          </Box>{' '}
          payment option from your checkout?
        </Typography>
        <Typography variant="body1" color="text.primary">
          You can always reconnect it later, but you&apos;ll need to go through the setup process
        </Typography>
        {error ? (
          <Typography color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1.5,
          justifyContent: 'flex-end',
          bgcolor: '#F9FAFB',
          borderTop: 'none',
        }}
      >
        <Button
          onClick={handleClose}
          disabled={submitting}
          variant="outlined"
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            '&&': {
              borderColor: '#E5E7EB',
              color: 'inherit',
              bgcolor: '#fff',
            },
            '&&:hover': {
              borderColor: '#D1D5DB',
              bgcolor: '#fff',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={submitting}
          variant="contained"
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            '&&': {
              bgcolor: DANGER_RED,
              color: '#fff',
              boxShadow: 'none',
            },
            '&&:hover': {
              bgcolor: DANGER_RED_HOVER,
              boxShadow: 'none',
            },
            '&&:disabled': {
              bgcolor: DANGER_RED,
              color: '#fff',
              opacity: 0.6,
            },
          }}
        >
          {submitting ? <CircularProgress size={22} color="inherit" /> : 'Yes, Disconnect'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentProviderDisconnectModal;
